import React from "react";
import { searchTwelveDataSymbol } from "../../services/api/http";
import { debounce } from "lodash";

const LOCAL_SYMBOLS = [
  ["AAPL", "Apple Inc."],
  ["MSFT", "Microsoft Corporation"],
  ["GOOGL", "Alphabet Inc."],
  ["AMZN", "Amazon.com, Inc."],
  ["META", "Meta Platforms, Inc."],
  ["NVDA", "NVIDIA Corporation"],
  ["TSLA", "Tesla, Inc."],
  ["BRK.B", "Berkshire Hathaway Inc."],
  ["JPM", "JPMorgan Chase & Co."],
  ["V", "Visa Inc."],
  ["UNH", "UnitedHealth Group Incorporated"],
  ["XOM", "Exxon Mobil Corporation"],
  ["LLY", "Eli Lilly and Company"],
  ["AVGO", "Broadcom Inc."],
  ["COST", "Costco Wholesale Corporation"],
  ["PG", "The Procter & Gamble Company"],
  ["HD", "The Home Depot, Inc."],
  ["MRK", "Merck & Co., Inc."],
  ["MA", "Mastercard Incorporated"],
  ["NFLX", "Netflix, Inc."],
  ["AMD", "Advanced Micro Devices, Inc."],
  ["ADBE", "Adobe Inc."],
  ["CRM", "Salesforce, Inc."],
  ["ORCL", "Oracle Corporation"],
  ["KO", "The Coca-Cola Company"],
  ["PEP", "PepsiCo, Inc."],
  ["INTU", "Intuit Inc."],
  ["BAC", "Bank of America Corporation"],
  ["WMT", "Walmart Inc."],
  ["QCOM", "QUALCOMM Incorporated"],
  ["DIS", "The Walt Disney Company"],
  ["PFE", "Pfizer Inc."],
  ["T", "AT&T Inc."],
  ["CSCO", "Cisco Systems, Inc."],
  ["IBM", "International Business Machines Corporation"],
  ["NKE", "NIKE, Inc."],
  ["GE", "GE Aerospace"],
  ["BA", "The Boeing Company"],
  ["SBUX", "Starbucks Corporation"],
  ["MCD", "McDonald’s Corporation"],
];

function makeLocalLookup(query) {
  const normalized = query.trim().toLowerCase();
  if (normalized.length < 1) return [];

  return LOCAL_SYMBOLS.filter(([symbol, name]) => {
    return (
      symbol.toLowerCase().includes(normalized) ||
      name.toLowerCase().includes(normalized)
    );
  }).map(([symbol, name]) => `${symbol}: ${name}`);
}

export default class Search extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      lookup: [],
      loading: false,
      selectedSymbol: "",
      selected: "",
      inputValue: "",
      activeIndex: -1,
      open: false,
    };

    this.debouncedSearch = debounce((value) => this.sendSymbolSearch(value), 350);
  }

  componentWillUnmount() {
    this.debouncedSearch.cancel();
  }

  onInputChange = (event) => {
    const value = event.target.value;
    const { onSelectionChange } = this.props;
    const localLookup = makeLocalLookup(value);
    this.setState(
      {
        inputValue: value,
        selected: value,
        selectedSymbol: "",
        open: true,
        activeIndex: -1,
        lookup: localLookup,
      },
      () => {
        if (onSelectionChange) {
          onSelectionChange(value);
        }
        this.debouncedSearch(value);
      }
    );
  };

  onKeyDown = (event) => {
    const { lookup, activeIndex, inputValue } = this.state;

    if (!lookup.length && event.key !== "Escape") return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.setState({
        activeIndex: Math.min(activeIndex + 1, lookup.length - 1),
        open: true,
      });
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.setState({
        activeIndex: Math.max(activeIndex - 1, 0),
        open: true,
      });
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const selection =
        lookup[activeIndex] || lookup[0] || inputValue.trim();
      if (selection) {
        this.selectItem(selection);
      }
      return;
    }

    if (event.key === "Escape") {
      this.setState({ open: false, activeIndex: -1 });
    }
  };

  selectItem = (value) => {
    const { onSelectionChange } = this.props;
    const symbol = value.split(":")[0].trim();
    this.setState(
      {
        selected: value,
        inputValue: value,
        selectedSymbol: symbol,
        open: false,
        activeIndex: -1,
      },
      () => {
        if (onSelectionChange) {
          onSelectionChange(symbol);
        }
      }
    );
  };

  sendSymbolSearch(symbol) {
    const query = (symbol || "").trim();

    if (query.length < 2) {
      this.setState({
        lookup: makeLocalLookup(query),
        loading: false,
        open: false,
        activeIndex: -1,
      });
      return;
    }

    const localLookup = makeLocalLookup(query);
    this.setState({ loading: true, open: true, lookup: localLookup, activeIndex: localLookup.length > 0 ? 0 : -1 });

    searchTwelveDataSymbol(query)
      .then((resp) => {
        const lookup = [...localLookup];
        const yahooQuotes = resp?.data?.data || [];

        if (yahooQuotes.length > 0) {
          yahooQuotes.forEach((item) => {
            if (!item.symbol) return;
            const name = item.instrument_name || item.name || item.symbol;
            const formatted = `${item.symbol}: ${name}`;
            if (!lookup.includes(formatted)) lookup.push(formatted);
          });
        }

        this.setState({
          lookup,
          loading: false,
          open: lookup.length > 0,
          activeIndex: lookup.length > 0 ? 0 : -1,
        });
      })
      .catch(() => {
        this.setState({
          lookup: localLookup,
          loading: false,
          open: localLookup.length > 0,
          activeIndex: localLookup.length > 0 ? 0 : -1,
        });
      });
  }

  render() {
    const { lookup, loading, inputValue, open, activeIndex, selectedSymbol } =
      this.state;
    return (
      <div className="financial-search-shell">
        <div className="financial-typeahead">
          <input
            id="ticker-search"
            className="financial-typeahead-input"
            type="text"
            value={inputValue}
            onChange={this.onInputChange}
            onKeyDown={this.onKeyDown}
            onFocus={() => this.setState({ open: lookup.length > 0 })}
            placeholder="Symbol search"
            aria-autocomplete="list"
            aria-controls="financial-typeahead-list"
          />
          {loading ? <div className="financial-typeahead-loading">Searching...</div> : null}
          {open && lookup.length > 0 ? (
            <div className="financial-typeahead-menu" id="financial-typeahead-list" role="listbox">
              {lookup.map((item, index) => (
                <button
                  key={item}
                  type="button"
                  className={`financial-typeahead-item${index === activeIndex ? " is-active" : ""}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    this.selectItem(item);
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="home-v3-actions" style={{ marginTop: "16px" }}>
          <button
            type="button"
            className="home-v3-button is-primary"
            onClick={() => this.props.onSearch(selectedSymbol || inputValue)}
          >
            Search
          </button>
        </div>
        <p
          className="meta-copy"
          style={{
            color: "var(--accent-2)",
            display: this.props.error ? "block" : "none",
            marginTop: "12px",
          }}
        >
          {this.props.error}
        </p>
      </div>
    );
  }
}
